import { __awaiter } from 'tslib';
import { BehaviorSubject } from 'rxjs';

/**
 * Atlas pages service, it can build a tree structured Org Node object and more.
 * Each node in object represents a org item.
 * If the Type has children (e.g. Space Group or Sites), they can be added under it.
 */
class AtlasPagesServiceHttpClient {
    constructor(mocked, session, http) {
        this.mocked = mocked;
        this.session = session;
        this.http = http;
        this.loginEndpoint = "links/login";
        this.forbiddenPath = "/forbidden/index.html";
        this.apiLocation = mocked ? "http://wiremock:8888/v1/" : "/api/v1/";
        this.headers = {
            Authorization: session,
            "Content-Type": "application/json",
        };
        console.log(`atlas-pages session: ${session}`);
        if (!session) {
            this.redirectToLogin();
        }
    }
    /**
     * HTTP GET method to call client.
     *
     * @param url - the API target for the paginated requests
     * @private - only to be used by this service
     */
    get(url) {
        const response = this.http.get(this.apiLocation + url, {
            headers: this.headers,
        });
        const isSuccess = (code) => code >= 200 && code < 300;
        return new Promise((resolve, reject) => {
            response.subscribe((res) => {
                if (res && isSuccess(res.code)) {
                    resolve(res);
                }
                else {
                    console.log("Error: API Service - unexpected response format");
                    reject(res);
                }
            }, (err) => {
                if (err.status === 401) {
                    return this.redirectToLogin();
                }
                else if (err.status === 403) {
                    return this.redirectToForbidden();
                }
                else if (err.status === 409) {
                    resolve(Object.assign(Object.assign({}, err.error), { status: err.status }));
                }
                else {
                    console.log("Error: API Service - " + JSON.stringify(err.error));
                    reject(err);
                }
            });
        });
    }
    redirectToForbidden() {
        return __awaiter(this, void 0, void 0, function* () {
            const redirectLocation = document.location.origin + this.forbiddenPath + "?type=forbidden";
            console.log("Redirecting to ", redirectLocation);
            // @ts-ignore
            document.location = redirectLocation;
            return Promise.resolve();
        });
    }
    redirectToLogin() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = document.location.pathname;
            const pathBrand = url.split("/")[1];
            let prezEndpoint = this.loginEndpoint;
            const { supportedBrands } = window;
            if (supportedBrands && supportedBrands.includes(pathBrand)) {
                prezEndpoint = `${this.loginEndpoint}/?brand=${pathBrand}`;
            }
            console.log("redirectToLogin - redirecting to endpoint: ", prezEndpoint);
            if (prezEndpoint) {
                return this.get(prezEndpoint).then((links) => {
                    console.log("found links for user: ", links);
                    const loginPage = links.LOGIN_PAGE
                        ? links.LOGIN_PAGE.reference
                        : links.reference;
                    // @ts-ignore
                    document.location = `${loginPage}&goto=${document.location}`;
                    return Promise.resolve();
                }, (err) => {
                    console.error("redirect threw error", err);
                    return Promise.reject(err);
                });
            }
            else {
                console.log("tried to redirect with a null endpoint");
            }
        });
    }
}

/**
 * Atlas pages service, it can build a tree structured Org Node object and more.
 * Each node in object represents a org item.
 * If the Type has children (e.g. Space Group or Sites), they can be added under it.
 */
const parentPropByTypeMap = {
    Portfolio: "program_id",
    Registration: "portfolio_id",
    Program: "operator_id",
    Product: "program_id",
};
const UNFILTERABLE = ['organization', 'admingroup'];
const MAX_SIMULTANEOUS_REQUESTS = 5;
class AtlasPagesService {
    constructor(mocked, session, httpClient) {
        this.mocked = mocked;
        this.session = session;
        this.httpClient = httpClient;
        this.currentTypesList = new Set();
        this.allTypesResponsesMap = new Map();
        this.hierarchy$ = new BehaviorSubject([]);
        this.httpClientService = new AtlasPagesServiceHttpClient(mocked, session, this.httpClient);
    }
    /**
     * Get all of the hierarchies the User has access to, starting at the specified top level to the lowest level.
     *
     * @param hierarchyTypesListTopToBottom - The list ordered from top to bottom of types for te hierarchy
     */
    getSpecificHierarchies(hierarchyTypesListTopToBottom, filteredBy) {
        return __awaiter(this, void 0, void 0, function* () {
            const allTypesMap = yield this.getFlattenedMapOfTypes(hierarchyTypesListTopToBottom, filteredBy);
            // reduce the map of hierarchies to a list with the given 'top' type
            const specificHierarchies = [];
            for (const [id, type] of allTypesMap) {
                // only collect the hierarchies for the top specified type
                specificHierarchies.push(type);
            }
            this.hierarchy$.next(specificHierarchies);
            return specificHierarchies;
        });
    }
    /**
     * Retrieves a list of Objects from Atlas.
     *
     * @param simpleType - the type of object to get from Atlas
     * @param page - the page number in the paginated list of Types
     */
    getPageForType(simpleType, page, filteredBy) {
        return __awaiter(this, void 0, void 0, function* () {
            let endpoint = `types/${simpleType}?page=${page}`;
            if (filteredBy) {
                endpoint += `&id=${filteredBy.id}&id_type=${filteredBy.dr_type}`;
            }
            const response = yield this.httpClientService.get(endpoint);
            if (response.code !== 200) {
                console.error(`types call failed for type: ${simpleType} on page: ${page} with this error: ${response.statusText}`);
                return { types: [] };
            }
            const { data, meta } = response;
            if (!data) {
                console.error("How did we get here without a response.data object o.O");
            }
            // NOTE: this is not locale safe and should not be used as a display_label to a User
            const capitalSimpleType = simpleType.charAt(0).toUpperCase() + simpleType.slice(1);
            const types = data.map((type) => {
                // required to continue to differentiate the levels as we mash them together in a flat map later
                // TODO: to clean this up, determine if a Space or DR Type before setting these properties.
                type.space_type = capitalSimpleType;
                type.spaceType = capitalSimpleType;
                type.dr_type = capitalSimpleType;
                type.drType = capitalSimpleType;
                type.type = capitalSimpleType;
                type.displayLabel = type.display_label || "";
                return type;
            });
            return { types, meta };
        });
    }
    /**
     * Fetches all of the pages for the given Type with the parents in the topology all in one flat array.
     *
     * @param type - the type of data to retrieve
     * @private
     */
    getAllPagesForType(type, filteredBy) {
        return __awaiter(this, void 0, void 0, function* () {
            const PAGE_SIZE = 1000;
            const { types, meta } = yield this.getPageForType(type, 1, filteredBy);
            let numPages = 1;
            let pagePromises = [];
            // calculate the number of pages we will call for
            if (meta && meta.hasOwnProperty(`total`)) {
                numPages = Math.ceil(meta.total / PAGE_SIZE);
            }
            if (numPages === 1) {
                types.sort((a, b) => { var _a; return (_a = a.display_label) === null || _a === void 0 ? void 0 : _a.localeCompare(b.display_label); });
                return { type, types };
            }
            // call for all pages past the 1st page (we already fetched the 1st page)
            // in blocks of maximum 5 simultaneous requests (to avoid errors for timeouts)
            let results = [];
            let pagesBlock = MAX_SIMULTANEOUS_REQUESTS;
            for (let page = 2; page <= numPages; page++) {
                pagePromises.push(this.getPageForType(type, page, filteredBy));
                if (page > pagesBlock) {
                    pagesBlock += MAX_SIMULTANEOUS_REQUESTS;
                    const prev = yield Promise.all(pagePromises);
                    results = [...results, ...prev];
                    pagePromises = [];
                }
            }
            const prev = yield Promise.all(pagePromises);
            results = [...results, ...prev];
            // break the pages apart and concatenate back into one list
            results = results.reduce((listOfTypesAndParents, result) => {
                if (result.hasOwnProperty(`types`)) {
                    listOfTypesAndParents.push(...result.types);
                }
                return listOfTypesAndParents;
            }, types);
            results.sort((a, b) => { var _a; return (_a = a.display_label) === null || _a === void 0 ? void 0 : _a.localeCompare(b.display_label); });
            return { type, types: results };
        });
    }
    /**
     * This fetches a list for each type in the hierarchy starting at the requested topType
     *
     * @param hierarchyTypesListTopToBottom - The list ordered from top to bottom of types for te hierarchy
     */
    getFlattenedMapOfTypes(hierarchyTypesListTopToBottom, filteredBy) {
        return __awaiter(this, void 0, void 0, function* () {
            // get types that have not been fetched
            const TYPES_TO_FETCH = hierarchyTypesListTopToBottom.filter((type) => !(this.currentTypesList.has(type) && (UNFILTERABLE.includes(type) || !filteredBy)));
            // get types that have been fetched
            const CACHED_TYPES = hierarchyTypesListTopToBottom.filter((type) => this.currentTypesList.has(type) && (UNFILTERABLE.includes(type) || !filteredBy));
            // fetch all of the missing types to build the hierarchy
            // one type at a time to not overload the service with multiple simultaneous requests
            let allTypesResponses = [];
            for (const type of TYPES_TO_FETCH) {
                const fetchedType = yield this.getAllPagesForType(type, filteredBy);
                allTypesResponses = [...allTypesResponses, fetchedType];
                if (!filteredBy || UNFILTERABLE.includes(type)) {
                    this.currentTypesList.add(type);
                }
            }
            const fetchedTypesMappedToId = new Map();
            const topTypesMappedToId = new Map();
            allTypesResponses.forEach((response) => {
                // create a mapping of all types to their ID
                this.allTypesResponsesMap.set(response.type, response.types);
                response.types.forEach((type) => fetchedTypesMappedToId.set(type.id, Object.assign({}, type)));
            });
            // Add cached types to the map of all types
            CACHED_TYPES.forEach((type) => {
                this.allTypesResponsesMap
                    .get(type)
                    .forEach((type) => fetchedTypesMappedToId.set(type.id, Object.assign({}, type)));
            });
            let topType = "";
            for (let type of hierarchyTypesListTopToBottom) {
                const response = this.allTypesResponsesMap.get(type);
                if (response.length > 0) {
                    topType = type;
                    break;
                }
            }
            // Use the map of Types to assign each type with a parent ID as a child of the corresponding parent
            fetchedTypesMappedToId.forEach((type, id) => {
                const parentProp = parentPropByTypeMap[type.type]
                    ? parentPropByTypeMap[type.type]
                    : "parent_id";
                const parent = type[parentProp];
                if (parent && parentProp !== "parent_id") {
                    type.parent_id = parent;
                }
                if (parent &&
                    parent.length > 0 &&
                    fetchedTypesMappedToId.get(parent) !== undefined) {
                    fetchedTypesMappedToId.get(parent).children =
                        fetchedTypesMappedToId.get(parent).children || [];
                    fetchedTypesMappedToId.get(parent).children.push(type);
                }
                if (type.type.toLowerCase() == topType) {
                    topTypesMappedToId.set(id, type);
                }
            });
            // return the tree of all types
            return topTypesMappedToId;
        });
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { AtlasPagesService };
